import type {ReactNode} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import {useTheme} from 'next-themes';
import {BarChart3, ChevronRight, Contrast, HeartHandshake, Lock, LogOut} from 'lucide-react';
import {
  AppearanceDialogContent,
  type AppearanceMode,
  AppSwitcher,
  type AppSwitcherItem,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogClose,
  DialogTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuPin,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebarPins,
  WellbeingDialogContent
} from '@kroll-resolver-crisp/ui-components-react';
import {isNavItemActive, type NavGroup, type NavItem, visibleNavGroups} from './nav';
import {FeedbackDialog} from './FeedbackDialog';
import {HelpDialog} from './HelpDialog';
import {READ_REPORTS_PERMISSION, useAuth} from '@/context/useAuth';
import {useModerationSettings, useReports} from '@/api/hooks';
import {rememberMode, useAppearancePreferences} from '@/lib/appearance';
import {deploymentLabel, PLATFORM_CODENAME, SWITCHER_APP_ID} from '@/lib/platform';
import {WELLBEING_HREFS} from '@/lib/wellbeing';

// The line under the name. Testing deployments mostly share an affiliate, so it is the host's own
// label (FALCON-TESTING, UITEST) rather than the codename.
const ORG_NAME = deploymentLabel(globalThis.location.hostname, PLATFORM_CODENAME);
const CURRENT_ORG_ID = ORG_NAME.toLowerCase();

// PRI is one app in the switcher, whatever the deployment: SWITCHER_APP_ID is the same everywhere
// and each deployment is an org under it (CURRENT_ORG_ID). The switcher folds entries sharing an id
// into one, with every deployment's orgs, so the menu shows PRI once with a link per deployment.
const PRI_APP: AppSwitcherItem = {
  id: SWITCHER_APP_ID,
  name: 'Platform Risk Intelligence',
  description: 'Tooling for investigations and submissions',
  href: '/',
  orgs: [{id: CURRENT_ORG_ID, name: ORG_NAME, href: '/'}]
};

const REPORTING_PATH = '/reporting';

// The persistent dialogs the design system puts in every app's sidebar footer. Wellbeing and
// Appearance bodies ship from the package; Feedback is composed locally (see FeedbackDialog.tsx)
// because PRI's API stores only free text. The trigger and the wiring are the app's.

function WellbeingDialog() {
  return (
    <Dialog>
      <SidebarMenuButton render={<DialogTrigger />} tooltip="Wellbeing">
        <HeartHandshake />
        <span>Wellbeing</span>
      </SidebarMenuButton>
      <WellbeingDialogContent hrefs={WELLBEING_HREFS} />
    </Dialog>
  );
}

function AppearanceDialog() {
  const {theme, setTheme} = useTheme();
  const value: AppearanceMode = theme === 'light' || theme === 'dark' ? theme : 'system';
  const {accent, setAccent, fontSize, setFontSize, corners, setCorners} =
    useAppearancePreferences();

  return (
    <Dialog>
      <SidebarMenuButton render={<DialogTrigger />} tooltip="Appearance">
        <Contrast />
        <span>Appearance</span>
      </SidebarMenuButton>
      {/* Every row applies as it is picked, so a single Done replaces the dialog's Cancel / Save. */}
      <AppearanceDialogContent
        value={value}
        accent={accent}
        fontSize={fontSize}
        corners={corners}
        footer={<DialogClose render={<Button variant="secondary" />}>Done</DialogClose>}
        onValueChange={(mode) => {
          // The class on <html> is next-themes'; the shared store carries the choice to the other
          // apps on the domain.
          setTheme(mode);
          rememberMode(mode);
        }}
        onAccentChange={setAccent}
        onFontSizeChange={setFontSize}
        onCornersChange={setCorners}
      />
    </Dialog>
  );
}

// A labelled group folds under its label. Settings starts folded unless the current page is in it.
// A folded group stays folded on the icon rail; expanding the sidebar is the way back to it.
function NavGroupSection({
  group,
  pathname,
  children
}: {
  readonly group: NavGroup;
  readonly pathname: string;
  readonly children: ReactNode;
}) {
  if (group.label === '') {
    return (
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>{children}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <Collapsible
      defaultOpen={
        !group.collapsedByDefault || group.items.some((item) => isNavItemActive(pathname, item.to))
      }
      render={<SidebarGroup />}>
      <SidebarGroupLabel render={<CollapsibleTrigger />} className="group/label">
        {group.label}
        <ChevronRight className="ml-auto transition-transform group-data-[panel-open]/label:rotate-90" />
      </SidebarGroupLabel>
      <CollapsibleContent>
        <SidebarGroupContent>
          <SidebarMenu>{children}</SidebarMenu>
        </SidebarGroupContent>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AppSidebar() {
  const {user, logout} = useAuth();
  const {pathname} = useLocation();
  const {data: moderationSettings} = useModerationSettings();
  const groups = visibleNavGroups(
    user?.role,
    user?.permissions,
    moderationSettings?.queues_enabled === true
  );
  const displayName = user?.displayName ?? 'Operator';
  const pins = useSidebarPins({storageKey: 'pri.sidebar.pins'});
  // Each report is a nav entry of its own under Reporting's flyout, so it can be pinned like any row.
  const {data: reports = []} = useReports(
    user?.permissions.includes(READ_REPORTS_PERMISSION) === true
  );
  const navItems = groups.flatMap((group) => group.items);
  const reportItems: NavItem[] = reports.map((report) => ({
    to: `${REPORTING_PATH}/${report.id}`,
    label: report.title,
    icon: BarChart3
  }));
  const pinnedItems = pins.pinned.flatMap((id) =>
    [...navItems, ...reportItems].filter((item) => item.to === id)
  );

  const renderPin = (item: NavItem) => (
    <SidebarMenuPin
      label={item.label}
      isPinned={pins.isPinned(item.to)}
      onPinnedChange={() => {
        pins.togglePinned(item.to);
      }}
    />
  );

  const renderLink = (item: NavItem, tooltip: string | undefined = item.label) => (
    <SidebarMenuButton
      render={<NavLink to={item.to} />}
      isActive={isNavItemActive(pathname, item.to, navItems)}
      tooltip={tooltip}>
      <item.icon />
      <span>{item.label}</span>
    </SidebarMenuButton>
  );

  // Reporting is a section: the row folds open to its reports, each with its own pin. Collapsed to
  // the icon rail, a click expands the sidebar with the section open and the row shows selected
  // while a report is current (DS 4.0.0, from isActive on the sub button). The row no longer
  // navigates, so "All reports" is the way to the reports homepage.
  const renderReporting = (item: NavItem) => (
    <Collapsible
      defaultOpen={isNavItemActive(pathname, REPORTING_PATH)}
      className="group/reporting">
      <CollapsibleTrigger render={<SidebarMenuButton tooltip={item.label} />}>
        <item.icon />
        <span>{item.label}</span>
        <ChevronRight className="ml-auto transition-transform group-data-[panel-open]/reporting:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <SidebarMenuSub>
          <SidebarMenuSubItem>
            <SidebarMenuSubButton
              render={<NavLink end to={REPORTING_PATH} />}
              isActive={pathname === REPORTING_PATH}>
              <span>All reports</span>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
          {reportItems.map((report) => (
            <SidebarMenuSubItem key={report.to}>
              <SidebarMenuSubButton
                render={<NavLink to={report.to} />}
                isActive={isNavItemActive(pathname, report.to, navItems)}>
                <span>{report.label}</span>
              </SidebarMenuSubButton>
              {renderPin(report)}
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );

  const renderItem = (item: NavItem) =>
    item.soon ? (
      <SidebarMenuItem key={item.to}>
        <SidebarMenuButton disabled tooltip={`${item.label} (soon)`}>
          <item.icon />
          <span>{item.label}</span>
        </SidebarMenuButton>
        <SidebarMenuBadge variant="soon">Soon</SidebarMenuBadge>
      </SidebarMenuItem>
    ) : (
      <SidebarMenuItem key={item.to}>
        {item.to === REPORTING_PATH && reportItems.length > 0
          ? renderReporting(item)
          : renderLink(item)}
        {item.readOnly ? (
          <SidebarMenuBadge aria-label="Read-only" title="Read-only">
            <Lock className="size-3" />
          </SidebarMenuBadge>
        ) : null}
        {renderPin(item)}
      </SidebarMenuItem>
    );

  const renderGroup = (group: NavGroup) => {
    const items = group.items.filter((item) => !pins.isPinned(item.to));
    if (items.length === 0) {
      return null;
    }

    // Editable settings above the divider, the fixed lists below it.
    const firstReference = items.findIndex((item) => item.reference);
    return (
      <NavGroupSection key={group.label} group={group} pathname={pathname}>
        {items.map((item, index) =>
          index === firstReference && index > 0
            ? [<SidebarSeparator key="reference" className="mx-2 my-1" />, renderItem(item)]
            : renderItem(item)
        )}
      </NavGroupSection>
    );
  };

  return (
    <Sidebar
      collapsible="icon"
      className="top-(--env-banner-height) h-[calc(100svh-var(--env-banner-height))]">
      {/* 52px, the design's, with the switcher's 44px row centred in it. The switcher carries the
          sidebar's collapse trigger, so nothing else sits here and nothing hides it on the icon rail;
          under 768px the page header's own trigger opens the drawer instead (PageHeader.tsx). */}
      <SidebarHeader className="h-13 justify-center py-0">
        {/* Recents are this browser's visit history, shared across every app on the domain. Each of
            crispthinking.com, crispqa.com and crispdev.com keeps its own list (ui-components-react
            4.0's shared cookie domains), so every build records itself (hasRecentApps is on by
            default): a testing deployment lands in the dev list, never in an analyst's live apps. Cleared on sign-out (AuthContext). */}
        <AppSwitcher apps={[PRI_APP]} currentAppId={PRI_APP.id} currentOrgId={CURRENT_ORG_ID} />
      </SidebarHeader>

      <SidebarContent>
        {/* Pinned rows sit under the unlabelled home row and above every labelled group. */}
        {groups.filter((group) => group.label === '').map(renderGroup)}
        {pinnedItems.length > 0 ? (
          <SidebarGroup>
            <SidebarGroupLabel>Pinned</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>{pinnedItems.map(renderItem)}</SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : null}
        {groups.filter((group) => group.label !== '').map(renderGroup)}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <HelpDialog />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <WellbeingDialog />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <FeedbackDialog />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <AppearanceDialog />
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          {/* One row is both the profile and the way out, as in the design: the name and role say
              who is signed in, and pressing it signs out. It hovers as every other row does (4.0's
              design dropped the destructive treatment). The role takes the rail's own muted ink;
              the page's text-muted-foreground is dark-on-dark on the brand gradient. */}
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              tooltip="Sign out"
              aria-label={`Sign out ${displayName}`}
              onClick={logout}>
              <LogOut />
              <span className="flex min-w-0 flex-col items-start leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate">{displayName}</span>
                <span className="text-sidebar-muted-foreground text-[10px] tracking-[0.06em] uppercase">
                  {user?.role || 'No role'}
                </span>
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      {/* The sidebar's edge collapses and expands it on click, as the reference shell has it. */}
      <SidebarRail />
    </Sidebar>
  );
}
